import apiClient from './apiService';
import type { AuditRecord } from '../components/common/AuditHistoryModal';
import {
    Holiday,
    WeeklyOffRule,
    LeaveType,
    LeaveBalance,
    LeaveApplication,
    ReportingMapRow,
    AttendanceDay,
    AttendancePunch,
    AttendanceSummaryRow,
} from './hrTypes';

// Shared active/inactive list filter across HR masters.
export type HrStatus = 'active' | 'inactive' | 'all';

// --- Holidays ---

export const listHolidays = async (year?: number, status: HrStatus = 'active'): Promise<Holiday[]> => {
    const params = new URLSearchParams();
    if (year) params.set('year', String(year));
    params.set('status', status);
    const response = await apiClient.get<{ items: Holiday[]; total: number }>(`/api/v1/hr/holidays?${params.toString()}`);
    return response.data.items ?? (response.data as any);
};

// Active/Inactive lifecycle toggle (is_active 1↔0) — separate from soft-delete.
export const setHolidayActive = async (id: number, active: boolean): Promise<Holiday> => {
    const response = await apiClient.patch<Holiday>(
        `/api/v1/hr/holidays/${id}/${active ? 'reactivate' : 'inactivate'}`,
    );
    return response.data;
};

export const createHoliday = async (data: Omit<Holiday, 'id'>): Promise<Holiday> => {
    const response = await apiClient.post<Holiday>('/api/v1/hr/holidays', data);
    return response.data;
};

export const deleteHoliday = async (id: number): Promise<void> => {
    await apiClient.delete(`/api/v1/hr/holidays/${id}`);
};

export const materializeWeeklyOffs = async (year: number): Promise<Holiday[]> => {
    const response = await apiClient.post<Holiday[]>(`/api/v1/hr/holidays/materialize-weekly-offs?year=${year}`);
    return response.data;
};

// --- Weekly-off Rule ---

export const getWeeklyOff = async (): Promise<WeeklyOffRule> => {
    const response = await apiClient.get<WeeklyOffRule>('/api/v1/hr/weekly-off-rule');
    return response.data;
};

export const setWeeklyOff = async (weekdays: number[]): Promise<WeeklyOffRule> => {
    const response = await apiClient.put<WeeklyOffRule>('/api/v1/hr/weekly-off-rule', { weekdays });
    return response.data;
};

// --- Leave Types ---

export type LeaveTypeStatus = 'active' | 'inactive' | 'all';

export const listLeaveTypes = async (status: LeaveTypeStatus = 'active'): Promise<LeaveType[]> => {
    const response = await apiClient.get<{ items: LeaveType[]; total: number }>(
        `/api/v1/hr/leave-types?status=${status}`,
    );
    return response.data.items ?? (response.data as any);
};

// Ungated dropdown source for self-service screens (e.g. staff Apply for Leave).
// No HR_LEAVE_TYPES RBAC gate — returns approved+active leave types only.
export const listLeaveTypesDropdown = async (): Promise<Pick<LeaveType, 'id' | 'name' | 'code'>[]> => {
    const response = await apiClient.get<{ items: Pick<LeaveType, 'id' | 'name' | 'code'>[] }>(
        '/api/v1/hr/leave-types/dropdown',
    );
    return response.data.items ?? [];
};

// Active/Inactive lifecycle toggle (is_active 1↔0) — separate from soft-delete.
export const setLeaveTypeActive = async (id: number, active: boolean): Promise<LeaveType> => {
    const response = await apiClient.patch<LeaveType>(
        `/api/v1/hr/leave-types/${id}/${active ? 'reactivate' : 'inactivate'}`,
    );
    return response.data;
};

export const createLeaveType = async (data: Omit<LeaveType, 'id'>): Promise<LeaveType> => {
    const response = await apiClient.post<LeaveType>('/api/v1/hr/leave-types', data);
    return response.data;
};

export const updateLeaveType = async (id: number, data: Partial<LeaveType>): Promise<LeaveType> => {
    const response = await apiClient.put<LeaveType>(`/api/v1/hr/leave-types/${id}`, data);
    return response.data;
};

export const deleteLeaveType = async (id: number): Promise<void> => {
    await apiClient.delete(`/api/v1/hr/leave-types/${id}`);
};

// --- Leave Types maker-checker workflow ---

export const submitLeaveType = async (id: number): Promise<void> => {
    await apiClient.post(`/api/v1/hr/leave-types/${id}/submit`);
};

export const approveLeaveType = async (id: number, note?: string): Promise<void> => {
    await apiClient.post(`/api/v1/hr/leave-types/${id}/approve`, { note });
};

export const rejectLeaveType = async (id: number, reason: string): Promise<void> => {
    await apiClient.post(`/api/v1/hr/leave-types/${id}/reject`, { reason });
};

export const reopenLeaveType = async (id: number): Promise<void> => {
    await apiClient.post(`/api/v1/hr/leave-types/${id}/reopen`);
};

export const getLeaveTypeHistory = async (id: number): Promise<AuditRecord[]> => {
    const response = await apiClient.get<{ items: any[] }>(`/api/v1/hr/leave-types/${id}/history`);
    const rows = response.data.items ?? (response.data as any) ?? [];
    // Map GRC_master_history rows into the AuditRecord shape the timeline expects.
    return rows.map((r: any) => ({
        audit_id: r.id,
        entity_id: r.entity_id,
        action: r.event_type === 'state_transition' ? 'STATE' : 'EDIT',
        changed_by: r.changed_by,
        changed_by_username: r.changed_by_username || 'Unknown',
        changed_on: r.changed_on,
        change_reason: r.remarks,
    })) as AuditRecord[];
};

// --- Leave Balances ---

export const listBalances = async (status: HrStatus = 'active'): Promise<LeaveBalance[]> => {
    const response = await apiClient.get<{ items: LeaveBalance[]; total: number }>(`/api/v1/hr/leave-balances?status=${status}`);
    return response.data.items ?? (response.data as any);
};

// Active/Inactive lifecycle toggle (is_active 1↔0) — separate from soft-delete.
export const setBalanceActive = async (id: number, active: boolean): Promise<LeaveBalance> => {
    const response = await apiClient.patch<LeaveBalance>(
        `/api/v1/hr/leave-balances/${id}/${active ? 'reactivate' : 'inactivate'}`,
    );
    return response.data;
};

export const myBalances = async (): Promise<LeaveBalance[]> => {
    const response = await apiClient.get<{ items: LeaveBalance[]; total: number }>('/api/v1/hr/leave-balances/me');
    return response.data.items ?? (response.data as any);
};

export const createBalance = async (data: Omit<LeaveBalance, 'id'>): Promise<LeaveBalance> => {
    const response = await apiClient.post<LeaveBalance>('/api/v1/hr/leave-balances', data);
    return response.data;
};

export const updateBalance = async (id: number, data: Partial<LeaveBalance>): Promise<LeaveBalance> => {
    const response = await apiClient.put<LeaveBalance>(`/api/v1/hr/leave-balances/${id}`, data);
    return response.data;
};

// --- Leave Balances maker-checker workflow ---

export const submitBalance = async (id: number): Promise<void> => {
    await apiClient.post(`/api/v1/hr/leave-balances/${id}/submit`);
};

export const approveBalance = async (id: number, note?: string): Promise<void> => {
    await apiClient.post(`/api/v1/hr/leave-balances/${id}/approve`, { note });
};

export const rejectBalance = async (id: number, reason: string): Promise<void> => {
    await apiClient.post(`/api/v1/hr/leave-balances/${id}/reject`, { reason });
};

export const reopenBalance = async (id: number): Promise<void> => {
    await apiClient.post(`/api/v1/hr/leave-balances/${id}/reopen`);
};

export const getBalanceHistory = async (id: number): Promise<AuditRecord[]> => {
    const response = await apiClient.get<{ items: any[] }>(`/api/v1/hr/leave-balances/${id}/history`);
    const rows = response.data.items ?? (response.data as any) ?? [];
    // Map GRC_master_history rows into the AuditRecord shape the timeline expects.
    return rows.map((r: any) => ({
        audit_id: r.id,
        entity_id: r.entity_id,
        action: r.event_type === 'state_transition' ? 'STATE' : 'EDIT',
        changed_by: r.changed_by,
        changed_by_username: r.changed_by_username || 'Unknown',
        changed_on: r.changed_on,
        change_reason: r.remarks,
    })) as AuditRecord[];
};

// --- Leave Applications ---

export const listLeaves = async (): Promise<LeaveApplication[]> => {
    const response = await apiClient.get<{ items: LeaveApplication[]; total: number }>('/api/v1/hr/leaves');
    return response.data.items ?? (response.data as any);
};

export const pendingApprovalsCount = async (): Promise<number> => {
    const response = await apiClient.get<{ pending: number }>('/api/v1/hr/leaves/approvals/count');
    return response.data.pending ?? 0;
};

export const createLeave = async (data: Omit<LeaveApplication, 'id' | 'workflow_state' | 'is_approved'>): Promise<LeaveApplication> => {
    const response = await apiClient.post<LeaveApplication>('/api/v1/hr/leaves', data);
    return response.data;
};

export const updateLeave = async (id: number, data: Partial<LeaveApplication>): Promise<LeaveApplication> => {
    const response = await apiClient.put<LeaveApplication>(`/api/v1/hr/leaves/${id}`, data);
    return response.data;
};

export const submitLeave = async (id: number): Promise<LeaveApplication> => {
    const response = await apiClient.post<LeaveApplication>(`/api/v1/hr/leaves/${id}/submit`);
    return response.data;
};

export const approveLeave = async (id: number, note?: string): Promise<LeaveApplication> => {
    const response = await apiClient.post<LeaveApplication>(`/api/v1/hr/leaves/${id}/approve`, { note });
    return response.data;
};

export const rejectLeave = async (id: number, reason: string): Promise<LeaveApplication> => {
    const response = await apiClient.post<LeaveApplication>(`/api/v1/hr/leaves/${id}/reject`, { reason });
    return response.data;
};

export const reopenLeave = async (id: number): Promise<LeaveApplication> => {
    const response = await apiClient.post<LeaveApplication>(`/api/v1/hr/leaves/${id}/reopen`);
    return response.data;
};

// --- Reporting Map ---

export const listReportingMap = async (): Promise<ReportingMapRow[]> => {
    const response = await apiClient.get<{ items: ReportingMapRow[]; total: number }>('/api/v1/hr/reporting-map');
    return response.data.items ?? (response.data as any);
};

export const assignManager = async (data: {
    staff_user_id: number;
    manager_user_id: number;
    effective_from?: string;
}): Promise<ReportingMapRow> => {
    const response = await apiClient.post<ReportingMapRow>('/api/v1/hr/reporting-map', data);
    return response.data;
};

export const deleteReportingMap = async (id: number): Promise<void> => {
    await apiClient.delete(`/api/v1/hr/reporting-map/${id}`);
};

// Active/Inactive lifecycle toggle (is_active 1↔0) — inactive ⇒ staff self-approving.
export const setReportingMapActive = async (id: number, active: boolean): Promise<ReportingMapRow> => {
    const response = await apiClient.patch<ReportingMapRow>(
        `/api/v1/hr/reporting-map/${id}/${active ? 'reactivate' : 'inactivate'}`,
    );
    return response.data;
};

// --- Attendance Days ---

export const listDays = async (from: string, to: string): Promise<AttendanceDay[]> => {
    const response = await apiClient.get<{ items: AttendanceDay[]; total: number }>(`/api/v1/hr/attendance/days?from=${from}&to=${to}`);
    return response.data.items ?? (response.data as any);
};

export const upsertDay = async (data: {
    attendance_date: string;
    status: string;
    remarks?: string;
}): Promise<AttendanceDay> => {
    const response = await apiClient.post<AttendanceDay>('/api/v1/hr/attendance/days', data);
    return response.data;
};

// --- Attendance Punches ---

export const listPunches = async (date: string): Promise<AttendancePunch[]> => {
    const response = await apiClient.get<{ items: AttendancePunch[]; total: number }>(`/api/v1/hr/attendance/punches?date=${date}`);
    return response.data.items ?? (response.data as any);
};

export const punch = async (data: {
    punch_type: string;
    latitude?: number;
    longitude?: number;
    location_label?: string;
}): Promise<AttendancePunch> => {
    const response = await apiClient.post<AttendancePunch>('/api/v1/hr/attendance/punches', data);
    return response.data;
};

// --- Attendance Summary ---

export const summary = async (from: string, to: string): Promise<AttendanceSummaryRow[]> => {
    const response = await apiClient.get<AttendanceSummaryRow[]>(`/api/v1/hr/attendance/summary?from=${from}&to=${to}`);
    return response.data;
};

// --- Named export object (mirrors orgService pattern) ---

export const hrService = {
    listHolidays,
    createHoliday,
    deleteHoliday,
    materializeWeeklyOffs,
    getWeeklyOff,
    setWeeklyOff,
    listLeaveTypes,
    createLeaveType,
    updateLeaveType,
    deleteLeaveType,
    submitLeaveType,
    approveLeaveType,
    rejectLeaveType,
    reopenLeaveType,
    getLeaveTypeHistory,
    listBalances,
    myBalances,
    createBalance,
    updateBalance,
    submitBalance,
    approveBalance,
    rejectBalance,
    reopenBalance,
    getBalanceHistory,
    listLeaves,
    pendingApprovalsCount,
    createLeave,
    updateLeave,
    submitLeave,
    approveLeave,
    rejectLeave,
    reopenLeave,
    listReportingMap,
    assignManager,
    deleteReportingMap,
    listDays,
    upsertDay,
    listPunches,
    punch,
    summary,
};
